/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { getMemberList } from "apis/members";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import Modal from "react-modal";
import { TMember } from "types/user.types";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onUpdate?: () => void;
  title: string;
  existedMembers?: TMember[];
  onHandleAddManager?: (selectedMembers: TMember[]) => void;
};

const AddMeetingManagerModal = (props: Props) => {
  const { modalIsOpen, closeModal, title, onHandleAddManager, existedMembers } = props;
  const [searchResult, setSearchResult] = useState<TMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<TMember[]>([]);

  const getSearchMemberList = async (search?: string) => {
    const res = await getMemberList(1, undefined, search);
    if (res.success) {
      setSearchResult(res.memberList ?? []);
    }
  };

  const onHandleAdd = async () => {
    onHandleAddManager?.(selectedMembers);
  };

  useEffect(() => {
    setSearchResult([]);
    setSelectedMembers([]);
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelIcon>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelIcon>
        <Title>
          <UI.Typography color="var(--alias-color-label-neutral)" type="title03" fontStyle="bold">
            {title}
          </UI.Typography>
        </Title>
        <Form>
          <UI.Input
            placeholder="이름, 이메일 검색"
            suffixIcon="search"
            showSuffixIcon={true}
            onChange={debounce((value) => {
              if (value.length > 0) getSearchMemberList(value);
              else setSearchResult([]);
            }, 500)}
          />
          <div className="chip-list">
            {selectedMembers.map((member: TMember, index: number) => (
              <UI.Chip.Contained
                key={member.id + `_${index}`}
                title={`${member.nickname} | ${member.email}`}
                rightIcon="x"
                showRightIcon
                onClick={() =>
                  setSelectedMembers(selectedMembers.filter((selectedMember) => selectedMember.id !== member.id))
                }
              />
            ))}
          </div>
          {searchResult.length > 0 && (
            <div className="member-list">
              {searchResult.map((member: TMember, index: number) => (
                <MemberItem
                  isExist={existedMembers?.find((existedMember) => existedMember.id === member.id) ? true : false}
                  selected={selectedMembers.find((selectedMember) => selectedMember.id === member.id) ? true : false}
                  onClick={() => {
                    if (selectedMembers.find((selectedMember) => selectedMember.id === member.id)) {
                      setSelectedMembers(selectedMembers.filter((selectedMember) => selectedMember.id !== member.id));
                    } else setSelectedMembers([...selectedMembers, member]);
                  }}
                  key={member.id + `_${index}`}
                  picture={member.image}
                  name={member.nickname}
                  email={member.email}
                />
              ))}
            </div>
          )}
          <UI.Button
            title="추가하기"
            onClick={onHandleAdd}
            size="large"
            disabled={selectedMembers.length === 0}
            style={{
              width: "100%",
            }}
          />
        </Form>
      </Container>
    </Modal>
  );
};

export default AddMeetingManagerModal;

const MemberItem = ({
  picture,
  name,
  email,
  onClick,
  selected = false,
  isExist = false,
}: {
  picture?: string;
  name: string;
  email: string;
  onClick?: () => void;
  selected?: boolean;
  isExist?: boolean;
}) => {
  return (
    <MemberItemContainer onClick={() => !isExist && onClick?.()} isExist={isExist}>
      {picture && picture !== "" ? (
        <img src={picture} />
      ) : (
        <div className="empty-picture">
          <UI.Icon name="person" size={16} color="var(--alias-color-label-assistive)" />
        </div>
      )}
      <UI.Typography
        type="body01 normal"
        fontStyle={selected ? "bold" : "regular"}
        color={isExist ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-normal)"}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </UI.Typography>
      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
        |
      </UI.Typography>
      <UI.Typography
        type="body01 normal"
        fontStyle="regular"
        color={isExist ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-alternative)"}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          marginRight: "auto",
        }}
      >
        {email}
      </UI.Typography>
      {isExist && (
        <UI.Typography type="label01" fontStyle="regular" color="var(--alias-color-label-alternative)">
          등록된 멤버
        </UI.Typography>
      )}
    </MemberItemContainer>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  width: 600px;
  padding: 40px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  overflow-y: auto;

  border-radius: 16px;
  background: var(--alias-color-background-normal-normal);

  cursor: pointer;
`;

const CancelIcon = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;

const Title = styled.div`
  display: flex;
  padding-bottom: 20px;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-bottom: 1px solid var(--alias-color-line-normal);
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;

  .member-list {
    display: flex;
    height: 200px;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
    overflow-y: auto;
    flex-direction: column;

    ::-webkit-scrollbar {
      width: 6px;
      background-color: white;
    }

    ::-webkit-scrollbar-thumb {
      background-color: var(--component-color-fill-strong);
      border-radius: 4px;
    }
  }

  .chip-list {
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    gap: 8px;
    align-self: stretch;
    flex-wrap: wrap;
  }
`;

const MemberItemContainer = styled.div<{ isExist: boolean }>`
  display: flex;
  padding: 8px 12px;
  height: 40px;
  box-sizing: border-box;
  align-items: center;
  gap: 6px;
  align-self: stretch;
  ${(props) => (props.isExist ? "background: var(--alias-color-interaction-disabled);" : "")}

  &:hover {
    background-color: var(--component-color-fill-normal);
  }

  .empty-picture {
    display: flex;
    width: 24px;
    height: 24px;
    justify-content: center;
    align-items: center;

    border-radius: 40px;
    background: var(--component-color-fill-alternative);
  }

  img {
    width: 24px;
    height: 24px;
    object-fit: cover;
    border-radius: 40px;
  }
`;
